import React, { useContext, useEffect, useState } from 'react';
import { Button, Empty, Modal, Spin, Tabs, Typography } from 'antd';

import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { V2_EVENTS_ENDPOINT } from '@totem/utilities/endpoints';

import './eventDetailsV2.css';

interface RawEvent {
  sourceSystem: string;
  sourceType: string;
  messageId: string;
  originalEvent: unknown;
  attributes: unknown;
}

type Props = {
  eventId: string;
  open: boolean;
  onClose: () => void;
};

const JsonBlock = ({ value }: { value: unknown }) => {
  const isEmpty =
    value === null ||
    value === undefined ||
    (Array.isArray(value) && value.length === 0);
  if (isEmpty) {
    return <Empty description="Nothing received from the source." />;
  }

  const text = JSON.stringify(value, null, 2);
  return (
    <>
      <Typography.Paragraph copyable={{ text }} styleName="raw-copy">
        Copy JSON
      </Typography.Paragraph>
      <pre styleName="raw-json">{text}</pre>
    </>
  );
};

// The event exactly as received from its source system. Internal system administrators only; the API enforces it.
const RawEventModal = ({ eventId, open, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [raw, setRaw] = useState<RawEvent>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setLoading(true);

    setFailed(false);

    fetch(`${V2_EVENTS_ENDPOINT}/dashboard/${eventId}/raw`, {
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`${res.status}`);
        }
        return res.json();
      })
      .then((result: RawEvent) => setRaw(result))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [eventId, open]);

  return (
    <Modal
      title="Raw event"
      open={open}
      onCancel={onClose}
      width={900}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {loading && (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <Spin />
        </div>
      )}
      {!loading && failed && (
        <Empty description="The raw event could not be loaded." />
      )}
      {!loading && !failed && raw && (
        <>
          <div styleName="facts" style={{ marginBottom: 12 }}>
            <span styleName="fact-label">Source system</span>
            <span>{raw.sourceSystem || '—'}</span>
            <span styleName="fact-label">Source type</span>
            <span>{raw.sourceType || '—'}</span>
            <span styleName="fact-label">Message id</span>
            <span>{raw.messageId || '—'}</span>
          </div>
          <Tabs
            items={[
              {
                key: 'original',
                label: 'Original event',
                children: <JsonBlock value={raw.originalEvent} />,
              },
              {
                key: 'attributes',
                label: 'Attributes',
                children: <JsonBlock value={raw.attributes} />,
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default RawEventModal;
